import React, { Component } from "react";
import styled from "@emotion/styled";
import Slide from "./Slide";
import PropTypes from "prop-types";

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: visible;
`;

const NavigationDots = styled.div`
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1000;
`;

const Dot = styled.div`
  width: ${props => props.active ? '8px' : '10px'};
  height: ${props => props.active ? '40px' : '10px'};
  border-radius: ${props => props.active ? '4px' : '50%'};
  background: ${props => props.active ? '#5A3232' : 'rgba(0, 0, 0, 0.2)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#5A3232' : 'rgba(0, 0, 0, 0.4)'};
    transform: ${props => props.active ? 'none' : 'scale(1.2)'};
  }
`;

const NavigationButtons = styled.div`
  position: relative;
  display: flex;

  height: 60px;
  margin: 0 auto;
  width: 20%;
  margin-top: 1rem;
  justify-content: space-between;
  z-index: 1000;
`;

const NavBtn = styled.div`
  background: white;
  padding: 15px;
  margin-bottom: 10px;
  border-radius: 3px;
`;

function mod(a, b) {
  return ((a % b) + b) % b;
}

class VerticalCarousel extends React.Component {
  state = {
    index: 0,
    goToSlide: null,
    prevPropsGoToSlide: 0,
    newSlide: false,
    isPaused: false
  };

  autoScrollInterval = null;

  componentDidMount = () => {
    document.addEventListener("keydown", event => {
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      if (event.keyCode === 38) {
        this.moveSlide(-1);
      }
      if (event.keyCode === 40) {
        this.moveSlide(1);
      }
    });

    // Start auto-scroll
    this.startAutoScroll();
  };

  componentWillUnmount = () => {
    // Clean up auto-scroll interval
    this.stopAutoScroll();
  };

  startAutoScroll = () => {
    const { autoScrollInterval = 3000 } = this.props;
    this.autoScrollInterval = setInterval(() => {
      if (!this.state.isPaused) {
        this.moveSlide(1);
      }
    }, autoScrollInterval);
  };

  stopAutoScroll = () => {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  };

  pauseAutoScroll = () => {
    this.setState({ isPaused: true });
  };

  resumeAutoScroll = () => {
    this.setState({ isPaused: false });
  };

  static propTypes = {
    slides: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.any,
        content: PropTypes.object
      })
    ).isRequired,
    goToSlide: PropTypes.number,
    showNavigation: PropTypes.bool,
    showDots: PropTypes.bool,
    offsetRadius: PropTypes.number,
    animationConfig: PropTypes.object,
    autoScrollInterval: PropTypes.number
  };

  static defaultProps = {
    offsetRadius: 2,
    animationConfig: { tension: 120, friction: 14 },
    autoScrollInterval: 3000,
    showDots: true
  };

  modBySlidesLength = index => {
    return mod(index, this.props.slides.length);
  };

  moveSlide = direction => {
    this.setState({
      index: this.modBySlidesLength(this.state.index + direction),
      goToSlide: null
    });
    // Pause auto-scroll briefly when user manually changes slide
    this.pauseAutoScroll();
    setTimeout(() => this.resumeAutoScroll(), 5000);
  };

  clampOffsetRadius(offsetRadius) {
    const { slides } = this.props;
    const upperBound = Math.floor((slides.length - 1) / 2);

    if (offsetRadius < 0) {
      return 0;
    }
    if (offsetRadius > upperBound) {
      return upperBound;
    }

    return offsetRadius;
  }

  getPresentableSlides() {
    const { slides } = this.props;
    const { index } = this.state;
    let { offsetRadius } = this.props;
    offsetRadius = this.clampOffsetRadius(offsetRadius);
    const presentableSlides = new Array();

    for (let i = -offsetRadius; i < 1 + offsetRadius; i++) {
      presentableSlides.push(slides[this.modBySlidesLength(index + i)]);
    }

    return presentableSlides;
  }

  goToSlide = (slideIndex) => {
    this.setState({
      index: slideIndex,
      goToSlide: null
    });
    // Pause auto-scroll briefly when user manually changes slide
    this.pauseAutoScroll();
    setTimeout(() => this.resumeAutoScroll(), 5000);
  };

  render() {
    const { animationConfig, offsetRadius, showNavigation, showDots, slides } = this.props;
    const { index } = this.state;

    let navigationButtons = null;
    if (showNavigation) {
      navigationButtons = (
        <NavigationButtons>
          <NavBtn onClick={() => this.moveSlide(1)}>&#8593;</NavBtn>
          <NavBtn onClick={() => this.moveSlide(-1)}>&#8595;</NavBtn>
        </NavigationButtons>
      );
    }
    return (
      <React.Fragment>
        <Wrapper
          onMouseEnter={this.pauseAutoScroll}
          onMouseLeave={this.resumeAutoScroll}
        >
          {this.getPresentableSlides().map((slide, presentableIndex) => (
            <Slide
              key={slide.key}
              content={slide.content}
              moveSlide={this.moveSlide}
              offsetRadius={this.clampOffsetRadius(offsetRadius)}
              index={presentableIndex}
              animationConfig={animationConfig}
            />
          ))}
          
          {/* Navigation Dots */}
          {showDots && (
            <NavigationDots>
              {slides.map((slide, dotIndex) => (
                <Dot
                  key={dotIndex}
                  active={dotIndex === index}
                  onClick={() => this.goToSlide(dotIndex)}
                />
              ))}
            </NavigationDots>
          )}
        </Wrapper>
        {navigationButtons}
      </React.Fragment>
    );
  }
}

export default VerticalCarousel;
